import { FC, useMemo, Fragment } from 'react';
// Hooks
import useMenu from 'app/hooks/useMenu';
// store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import { AutomatedWorkflowsActions } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.slice';
// Selectors
import { selectSort } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.selectors';
// Mui
import { Box, Divider, Tooltip } from '@mui/material';
// Icons
import { Check as CheckIcon } from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';
import Icon from 'app/components/Icon';
// i18next
import { useTranslation } from 'react-i18next';

interface ISortOption {
  name: string;
  sortBy: 'name' | 'executedOn';
  direction: 'asc' | 'desc';
  label: string;
}

const WorkflowsSorting:FC = () => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // Selectors
  const sort = useAppSelector(selectSort);

  const setSort = (sortBy: ISortOption['sortBy'], direction: ISortOption['direction']) => {
    dispatch(AutomatedWorkflowsActions.setSort({ sortBy, direction }));
  }

  const { Menu, MenuItem, openMenu } = useMenu();

  const sortOptions:ISortOption[] = useMemo(() => [
    { name: 'sort-by-name-asc', sortBy: 'name', direction: 'asc', label: t('components.workflowsSorting.workflowNameAtoZ') },
    { name: 'sort-by-name-desc', sortBy: 'name', direction: 'desc', label: t('components.workflowsSorting.workflowNameZtoA') },
    { name: 'sort-by-last-executed-date-desc', sortBy: 'executedOn', direction: 'desc', label: t('components.workflowsSorting.executedOnNewToOld') },
    { name: 'sort-by-last-executed-date-asc', sortBy: 'executedOn', direction: 'asc', label: t('components.workflowsSorting.executedOnOldToNew') }
  ], [t]);

  return (
    <Fragment>
      <Tooltip title={t('labels.sort')}>
        <div>
          <IconButton
            name="workflow-sort-button"
            onClick={openMenu}
            size="small"
          >
            <Icon
              icon="sort-amount-desc"
              rootSx={{ p: 0.5 }}
              sx={{ width: '20px', height: '20px', fontSize: '20px', transform: 'rotateX(180deg)' }}
            />
          </IconButton>
        </div>
      </Tooltip>
      <Menu>
        {sortOptions.map(({ name, sortBy, direction, label }, index) => {
          const showDivider = (index + 1) % 2 === 0 && index !== sortOptions.length - 1;
          const selected = sort.sortBy === sortBy && sort.direction === direction;
          return (
            <Fragment key={name}>
              <MenuItem
                key={name} name={name}
                onClick={() => setSort(sortBy, direction)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
              >
                {selected ? <CheckIcon sx={{ fontSize: 16 }} /> : <Box sx={{ width: 16 }} />}
                {label}
              </MenuItem>
              {showDivider ? <Divider /> : null}
            </Fragment>
          )
        })}
      </Menu>
    </Fragment>
  )
}

export default WorkflowsSorting;
